// Overworld NPCs; the wandering cast of Domino City and Duel Academy, recreated
// from Yu-Gi-Oh! ONLINE 2 (Duel Evolution).
//
// The original game stored NPC placement server-side / in the executable, so the
// exact coordinates are not recoverable from the asset dump. What IS recovered is
// the cast itself: the chibi sprites (DATA/avatar/chibi/cn_<preset>); the
// a-series belongs to Duel Academy (GX), the d-series to Domino City (DM). We
// honour that region split and stand each character on the matching map's art at a
// hand-placed spot, the way townsfolk/duelists dotted the maps in the original.
//
// We place ONLY characters we can identify as real, named Yu-Gi-Oh! duelists (no
// generic filler). Dialog is written in the spirit of the Tag Force games;
// short, in-character greetings ending in a duel challenge.
//
// `char` is the character id shared by the in-world chibi (chibi.json npc preset)
// and the dialog bust (npcs.json bust id). Clicking an NPC opens a dialog that
// shows the full bust + these lines.

import type { Facing } from "@ygoevo/world-protocol";

export interface WorldNpc {
  /** Stable per-placement id (unique across the whole world). */
  id: string;
  /** Character id; chibi preset (in-world) + bust id (dialog). */
  char: string;
  /** Display name shown in the dialog header. */
  name: string;
  /** Short subtitle under the name (faction / role). */
  title: string;
  /** Feet position in world px on this map. */
  x: number;
  y: number;
  /** Which way the chibi faces while standing. Default "s" (toward camera). */
  facing?: Facing;
  /** Speech lines, shown one per "Next" tap. */
  lines: string[];
  /** Whether the dialog offers a duel (wired to the practice-duel action). */
  canDuel?: boolean;
}

// ── Domino City (DM cast) ────────────────────────────────────────────────────
const DOMINO: Record<string, WorldNpc[]> = {
  // Station Square; the city hub.
  map_d01_01: [
    {
      id: "d_yami_yugi",
      char: "d01",
      name: "Yami Yugi",
      title: "King of Games",
      x: 980,
      y: 980,
      facing: "e",
      lines: [
        "It's good to see you again.",
        "The bonds between duelists are a power all their own.",
        "When you're ready to test that power; let's duel.",
      ],
      canDuel: true,
    },
    {
      id: "d_joey",
      char: "d07",
      name: "Joey Wheeler",
      title: "Rising Duelist",
      x: 1580,
      y: 980,
      facing: "w",
      lines: [
        "Hey, pal! Lookin' for a duel?",
        "I've been on a real hot streak lately, ya know!",
        "C'mon; let's see what you've got!",
      ],
      canDuel: true,
    },
  ],
  // Card Shop Street.
  map_d02: [
    {
      id: "d_tea",
      char: "d06",
      name: "Téa Gardner",
      title: "Friend & Supporter",
      x: 1024,
      y: 820,
      lines: [
        "Hey! Good to see you.",
        "Never give up; believe in your deck and your friends.",
        "Now go show everyone what you've got!",
      ],
    },
  ],
  // Plaza / harbour.
  map_d03: [
    {
      id: "d_marik",
      char: "d08",
      name: "Marik Ishtar",
      title: "Keeper of the Rod",
      x: 820,
      y: 950,
      lines: [
        "Heh... so you've come.",
        "The power of the Millennium Items is not to be trifled with.",
        "Let me show you true darkness; duel me.",
      ],
      canDuel: true,
    },
    {
      id: "d_bakura",
      char: "d09",
      name: "Bakura",
      title: "Occult Duelist",
      x: 1240,
      y: 950,
      facing: "w",
      lines: [
        "Oh, hello. Fancy meeting you here.",
        "My Occult deck is always hungry for a new challenger...",
        "Care to play a little game?",
      ],
      canDuel: true,
    },
  ],
  // Park.
  map_d04_01: [
    {
      id: "d_mai",
      char: "d10",
      name: "Mai Valentine",
      title: "Harpie Duelist",
      x: 1024,
      y: 900,
      lines: [
        "Well, if it isn't you.",
        "A lady never reveals all her cards, sweetie.",
        "Think you can handle my Harpies? Let's duel.",
      ],
      canDuel: true,
    },
  ],
  // KaibaCorp Stadium.
  map_d05: [
    {
      id: "d_kaiba",
      char: "d04",
      name: "Seto Kaiba",
      title: "CEO, KaibaCorp",
      x: 512,
      y: 600,
      lines: [
        "You're wasting my time.",
        "I only duel opponents worth my attention.",
        "Prove you're one of them. Duel me.",
      ],
      canDuel: true,
    },
  ],
};

// ── Duel Academy (GX cast) ───────────────────────────────────────────────────
const ACADEMY: Record<string, WorldNpc[]> = {
  // Academy entrance.
  map_a01: [
    {
      id: "a_jaden",
      char: "a01",
      name: "Jaden Yuki",
      title: "Slifer Red",
      x: 820,
      y: 900,
      lines: [
        "Hey hey! What's up?",
        "Dueling's all about having fun, ya know!",
        "So whaddaya say; get your game on!",
      ],
      canDuel: true,
    },
    {
      id: "a_jesse",
      char: "a05",
      name: "Jesse Andersen",
      title: "North Academy",
      x: 1240,
      y: 900,
      facing: "w",
      lines: [
        "Howdy! Me and Ruby were just lookin' around.",
        "My Crystal Beasts ain't just cards; they're family.",
        "Wanna meet 'em? Let's throw down!",
      ],
      canDuel: true,
    },
  ],
  // Courtyard.
  map_a02: [
    {
      id: "a_alexis",
      char: "a03",
      name: "Alexis Rhodes",
      title: "Obelisk Blue",
      x: 1024,
      y: 820,
      lines: [
        "Welcome to Duel Academy.",
        "Talent only takes you so far; skill wins duels.",
        "Show me yours. Let's duel.",
      ],
      canDuel: true,
    },
  ],
  // Arena.
  map_a03: [
    {
      id: "a_zane",
      char: "a06",
      name: "Zane Truesdale",
      title: "Obelisk Blue; Kaiser",
      x: 1024,
      y: 820,
      lines: [
        "So you've found your way to the arena.",
        "I am the Kaiser; Duel Academy's finest.",
        "Show me you belong here. Duel me.",
      ],
      canDuel: true,
    },
  ],
  // Dorm.
  map_a04: [
    {
      id: "a_chazz",
      char: "a04",
      name: "Chazz Princeton",
      title: "Obelisk Blue",
      x: 1024,
      y: 1180,
      lines: [
        "You talkin' to the Chazz?",
        "I'm gonna be the next King of Games, got it?",
        "Fine; I'll duel ya. Don't even blink.",
      ],
      canDuel: true,
    },
  ],
};

const MAP_NPCS: Record<string, WorldNpc[]> = { ...DOMINO, ...ACADEMY };

/** NPCs that stand on the given map (empty when none). */
export function npcsFor(mapId: string): WorldNpc[] {
  return MAP_NPCS[mapId] ?? [];
}

/** Find an NPC by its character id ("d01".."a06") across every map. */
export function findNpcByChar(char: string): WorldNpc | undefined {
  for (const list of Object.values(MAP_NPCS)) {
    const hit = list.find((n) => n.char === char);
    if (hit) return hit;
  }
  return undefined;
}

/** Every named NPC once (deduped by `char`) — the full duelable cast. Used by the Free Duel bot
 *  picker; each `char` has a signature deck server-side (apps/world/src/npcDecks.ts). */
export function allNpcs(): WorldNpc[] {
  const seen = new Set<string>();
  const out: WorldNpc[] = [];
  for (const list of Object.values(MAP_NPCS)) {
    for (const n of list) if (!seen.has(n.char)) { seen.add(n.char); out.push(n); }
  }
  return out;
}
